import React, { useMemo, useState } from 'react';
import { Avatar, Badge, Box, Center, Group, Loader, Modal, Paper, ScrollArea, Stack, Text, TextInput, ThemeIcon, Timeline } from '@mantine/core';
import { IconArrowNarrowRight, IconHistory, IconSearch, IconTrendingDown, IconTrendingUp } from '@tabler/icons-react';
import type { ICambioVariable } from '../../../modelos/CotizadorNBS';

interface ModalHistorialCambiosVistaProps {
    abierto: boolean;
    alCerrar: () => void;
    /** null mientras carga. */
    cambios: ICambioVariable[] | null;
    /** Qué historial es (p. ej. "Configuración global", "Variables de la cotización"); va debajo del título. */
    subtitulo: string;
    /** Moneda de los viáticos (COP en Colombia, USD en países en dólares). */
    monedaLocal: string;
}

// ---- Unidad de cada variable (por su clave). Lo demás en dinero va en USD.
const PORCENTAJES = /^(Margen|Descuento|Porcentaje)/;
const EN_MONEDA_LOCAL = new Set(['ValorViaticoDiaPersona', 'CostoLocalValorTransportePorDia', 'ValorPromedioVuelo']);
const UNIDADES_FIJAS: Record<string, string> = {
    HorasDiaInstalacion: 'h',
    CantidadTecnicos: 'técnicos',
    MesesMantenimiento: 'meses',
    MesesMantenimientoGratis: 'meses',
};

/** Valor sin formato del back (p. ej. "3600.5") con formato del país y su unidad: "$ 3.600,5 COP", "25 %", "8 h". */
const conUnidad = (clave: string, texto: string | null, monedaLocal: string) => {
    if (texto === null || texto === '') return '—';
    const numero = Number(texto);
    if (Number.isNaN(numero)) return texto;
    const valor = numero.toLocaleString('es-CO', { maximumFractionDigits: 4 });
    if (PORCENTAJES.test(clave)) return `${valor} %`;
    if (UNIDADES_FIJAS[clave]) return `${valor} ${UNIDADES_FIJAS[clave]}`;
    if (clave === 'Trm') return `$ ${valor} COP`;
    if (clave === 'TransformadorKw') return `$ ${valor} USD / kW`;
    return `$ ${valor} ${EN_MONEDA_LOCAL.has(clave) ? monedaLocal || 'COP' : 'USD'}`;
};

const fecha = (utc: string) =>
    new Date(utc.endsWith('Z') ? utc : `${utc}Z`).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });

const iniciales = (usuario: string) => {
    const partes = usuario.split(/[\s@._]+/).filter(Boolean);
    return ((partes[0]?.[0] ?? '?') + (partes[1]?.[0] ?? '')).toUpperCase();
};

/** Un guardado: los cambios que hizo un usuario en el mismo momento. */
interface Guardado { clave: string; usuario: string; fecha: string; cambios: ICambioVariable[] }

/** Historial de cambios de variables: línea de tiempo por guardado, con valor anterior → nuevo y si subió o bajó. */
export const ModalHistorialCambiosVista: React.FC<ModalHistorialCambiosVistaProps> = ({ abierto, alCerrar, cambios, subtitulo, monedaLocal }) => {
    const [busqueda, setBusqueda] = useState('');

    const guardados = useMemo<Guardado[]>(() => {
        const texto = busqueda.trim().toLowerCase();
        const filtrados = (cambios ?? []).filter(c => !texto
            || c.Variable.toLowerCase().includes(texto) || c.Usuario.toLowerCase().includes(texto));
        // Ya vienen del más reciente al más antiguo; se agrupan los de un mismo guardado (misma fecha y usuario).
        const grupos: Guardado[] = [];
        for (const cambio of filtrados) {
            const clave = `${cambio.Fecha}|${cambio.Usuario}`;
            const ultimo = grupos[grupos.length - 1];
            if (ultimo?.clave === clave) ultimo.cambios.push(cambio);
            else grupos.push({ clave, usuario: cambio.Usuario, fecha: cambio.Fecha, cambios: [cambio] });
        }
        return grupos;
    }, [cambios, busqueda]);

    return (
        <Modal
            opened={abierto}
            onClose={alCerrar}
            styles={{ header: { backgroundColor: '#0074d914' } }}
            title={
                <Group gap="xs">
                    <ThemeIcon color="primary" size="md" variant="light" radius="md">
                        <IconHistory size={18} />
                    </ThemeIcon>
                    <Box>
                        <Text fw={600} fz="md" lh={1.2}>Historial de Cambios</Text>
                        <Text size="xs" c="dimmed">{subtitulo}</Text>
                    </Box>
                </Group>
            }
            size="48rem"
            radius="md"
            centered
        >
            {cambios === null ? (
                <Center py="xl"><Loader size="sm" /></Center>
            ) : cambios.length === 0 ? (
                <Stack align="center" gap={6} py="xl">
                    <ThemeIcon size={44} radius="xl" variant="light" color="gray"><IconHistory size={24} /></ThemeIcon>
                    <Text size="sm" c="dimmed">Todavía no hay cambios registrados.</Text>
                </Stack>
            ) : (
                <Stack gap="sm" mt="xs">
                    <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                        <TextInput
                            size="xs"
                            radius="xl"
                            placeholder="Buscar variable o usuario"
                            leftSection={<IconSearch size={14} />}
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.currentTarget.value)}
                            style={{ flex: 1, minWidth: 220, maxWidth: 320 }}
                        />
                        <Text size="xs" c="dimmed">
                            {cambios.length} {cambios.length === 1 ? 'cambio' : 'cambios'}
                        </Text>
                    </Group>

                    <ScrollArea h={460} type="auto" scrollbarSize={8} offsetScrollbars>
                        {guardados.length === 0 ? (
                            <Text size="sm" c="dimmed" ta="center" py="lg">No hay cambios que coincidan con la búsqueda.</Text>
                        ) : (
                            <Timeline bulletSize={30} lineWidth={2} active={guardados.length} color="primary" pt={4} pr="xs">
                                {guardados.map(guardado => (
                                    <Timeline.Item
                                        key={guardado.clave}
                                        bullet={<Avatar size={30} radius="xl" color="primary" variant="filled">{iniciales(guardado.usuario)}</Avatar>}
                                        title={
                                            <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                                                <Box>
                                                    <Text size="sm" fw={600} lh={1.2}>{guardado.usuario}</Text>
                                                    <Text size="xs" c="dimmed">{fecha(guardado.fecha)}</Text>
                                                </Box>
                                                <Badge variant="light" color="primary" radius="xl" style={{ textTransform: 'none' }}>
                                                    {guardado.cambios.length} {guardado.cambios.length === 1 ? 'cambio' : 'cambios'}
                                                </Badge>
                                            </Group>
                                        }
                                    >
                                        <Stack gap={6} mt="xs" mb="md">
                                            {guardado.cambios.map(cambio => {
                                                const antes = Number(cambio.ValorAnterior);
                                                const despues = Number(cambio.ValorNuevo);
                                                const comparables = cambio.ValorAnterior !== null && cambio.ValorNuevo !== null
                                                    && !Number.isNaN(antes) && !Number.isNaN(despues);
                                                const sube = comparables && despues > antes;
                                                const baja = comparables && despues < antes;
                                                return (
                                                    <Paper key={cambio.Id} withBorder radius="md" px="sm" py={8} bg="var(--mantine-color-gray-0)">
                                                        <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                                                            <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0 }} truncate>{cambio.Variable}</Text>
                                                            <Group gap={6} wrap="nowrap">
                                                                <Text size="xs" c="dimmed" td={cambio.ValorAnterior !== null ? 'line-through' : undefined}>
                                                                    {conUnidad(cambio.Clave, cambio.ValorAnterior, monedaLocal)}
                                                                </Text>
                                                                <IconArrowNarrowRight size={14} color="var(--mantine-color-gray-5)" />
                                                                <Text size="sm" fw={700}>{conUnidad(cambio.Clave, cambio.ValorNuevo, monedaLocal)}</Text>
                                                                {(sube || baja) && (
                                                                    <ThemeIcon size={20} radius="xl" variant="light" color={sube ? 'teal' : 'red'}>
                                                                        {sube ? <IconTrendingUp size={13} /> : <IconTrendingDown size={13} />}
                                                                    </ThemeIcon>
                                                                )}
                                                            </Group>
                                                        </Group>
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    </Timeline.Item>
                                ))}
                            </Timeline>
                        )}
                    </ScrollArea>
                </Stack>
            )}
        </Modal>
    );
};
